import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@context/AuthContext";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@lib/firebase";
import { jsPDF } from "jspdf";

export const SuratPernyataan = () => {
  const { user } = useAuth();
  const { eventId } = useParams(); // Ambil eventId dari URL
  const [namaLengkap, setNamaLengkap] = useState("");
  const [tanggal, setTanggal] = useState("");
  const [eventData, setEventData] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [lang, setLang] = useState("id"); // "id" atau "en"
  const printRef = useRef(null);

  // === MAPPING EVENT DETAILS UNTUK MOUNTAIN MARATHON FESTIVAL TRILOGY ===
  const getEventDetails = (eventId) => {
    const eventDetailsMap = {
      JMM: {
        namaEvent: "Java Mountain Marathon (JMM)",
        lokasi: {
          id: "Jawa Barat",
          en: "West Java",
        },
        tahun: "2026",
        tipeKegiatan: "mountain marathon (running)",
      },
      SMM: {
        namaEvent: "Sembalun Mountain Marathon (SMM)",
        lokasi: {
          id: "Lombok, Nusa Tenggara Barat",
          en: "Lombok, West Nusa Tenggara",
        },
        tahun: "2026",
        tipeKegiatan: "mountain marathon (running)",
      },
      BMM: {
        namaEvent: "Bali Mountain Marathon (BMM)",
        lokasi: {
          id: "Bali",
          en: "Bali",
        },
        tahun: "2026",
        tipeKegiatan: "mountain marathon (running)",
      },
    };
    return eventDetailsMap[eventId] || eventDetailsMap["JMM"];
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setNamaLengkap(
            data.namaLengkap || user.displayName || "Nama Tidak Ditemukan"
          );
        } else {
          setNamaLengkap(user.displayName || "Nama Tidak Ditemukan");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserData();

    const today = new Date();
    const options = { day: "2-digit", month: "long", year: "numeric" };
    setTanggal(today.toLocaleDateString("id-ID", options));

    // Set event data
    if (eventId) {
      const details = getEventDetails(eventId);
      setEventData(details);
    }
  }, [user, eventId]);

  useEffect(() => {
    if (printRef.current && namaLengkap && tanggal && eventData) {
      setIsReady(true);
    }
  }, [namaLengkap, tanggal, eventData]);

  // Helper: get localized date string
  const formatDateForLang = (dateObj, language) => {
    if (!dateObj) return "";
    const opts = { day: "2-digit", month: "long", year: "numeric" };
    return dateObj.toLocaleDateString(
      language === "en" ? "en-US" : "id-ID",
      opts
    );
  };

  // Prepare paragraphs for both languages, dynamic but WITHOUT subtitle/deskripsi
  const buildContent = (language) => {
    if (!eventData)
      return { title: "", subtitle: null, paragraphs: [], nameLabel: "", dateLabel: "" };

    const nameLabel = language === "en" ? "Name" : "Nama";
    const dateLabel = language === "en" ? "Date" : "Tanggal";

    // SMM-specific waiver
    if (eventId === "SMM") {
      const smmSubtitle = "SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026";
      const smmNumberedID = [
        "Saya menyatakan dalam keadaan sehat secara fisik untuk mengikuti lomba SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026.",
        "Saya menyatakan, segala bentuk cedera, penyakit atau cacat fisik yang terjadi sebelum lomba berlangsung, dimana Saya mengakui cedera tersebut menjadi tanggung jawab saya.",
        "Saya menyadari bahwa lomba lari SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026 berpotensi secara fisik menyebabkan saya cedera atau meninggal dunia selama lomba berlangsung. Jika Saya mengetahui adanya gangguan medis yang dapat mengganggu Saya saat lomba, maka Saya akan mengundurkan diri untuk mencegah bahaya cedera atau kondisi medis lainnya.",
        "Saya menyatakan berpartisipasi pada lomba SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026 dengan pertimbangan mampu secara medis dan sudah terlatih dengan baik. Sehingga Saya bertanggung jawab penuh atas cedera atau kecelakaan dalam bentuk apapun yang mungkin terjadi saat Saya bepergian menuju ke tempat acara lomba, selama lomba, atau sepulang dari lomba.",
        "Saya menyatakan setuju untuk mendapatkan pertolongan medis diluar kemampuan dari tim medis lomba, dimana Penyelenggara lomba SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026 dapat mengatur evakuasi darurat ataupun perawatan yang dianggap perlu dengan segala biaya menjadi tanggung jawab Saya.",
        "Saya menyatakan setuju dan bertanggung jawab atas asuransi kesehatan Saya sendiri.",
        "Saya atas nama Saya sendiri dan ahli waris menyatakan melepaskan penyelenggara lomba SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026, sponsor, race management, dan semua orang yang berkaitan dengan lomba SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026 dari tanggung jawab atas semua klaim, tindakan atau kerusakan baik fisik maupun non fisik yang mungkin timbul dari partisipasi Saya selama lomba.",
        "Saya menyatakan setuju bahwa informasi dalam bentuk dokumen pribadi yang termasuk didalamnya riwayat kesehatan yang dikumpulkan pihak penyelenggara sebagai bagian dari proses mengikuti lomba dan kebutuhan dalam keadaan darurat medis.",
        "Saya menyatakan setuju untuk memenuhi segala bentuk aturan dan arahan yang diberikan oleh penyelenggara SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026.",
        "Saya menyatakan memahami bahwa penyelenggara dapat mengubah rute jika diperlukan tanpa pemberitahuan sebelumnya.",
        "Saya menyatakan setuju untuk membawa semua perlengkapan yang diwajibkan kepada Saya selama lomba dan mematuhi aturan yang ada di lingkungan lomba diadakan.",
      ];
      const smmNumberedEN = [
        "I declare that I am in good physical health to participate in the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026.",
        "I declare that any form of injury, illness, or physical disability occurring before the race is my sole responsibility.",
        "I am aware that the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026 is a physically demanding event with the potential for injury or death during the race. If I become aware of any medical condition that could interfere with my performance, I will withdraw to prevent the risk of injury or other medical complications.",
        "I declare that I am participating in the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026 with the consideration that I am medically fit and well-trained. Therefore, I take full responsibility for any injury or accident of any kind that may occur while traveling to the venue, during the race, or returning from the event.",
        "I agree to receive medical assistance beyond the capacity of the event\u2019s medical team, whereby the Organizers of the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026 may arrange for emergency evacuation or necessary treatment, with all associated costs being my responsibility.",
        "I agree and acknowledge that I am responsible for my own health insurance.",
        "On behalf of myself and my heirs, I hereby release the organizers of the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026, sponsors, race management, and all persons associated with the event from liability for all claims, actions, or damages\u2014both physical and non-physical\u2014that may arise from my participation in the race.",
        "I agree that personal information, including medical history, may be collected by the organizers as part of the registration process and for use in medical emergencies.",
        "I agree to comply with all rules and directions provided by the organizers of the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026.",
        "I understand that the organizers may change the route, if necessary, without prior notice.",
        "I agree to carry all mandatory equipment during the race and to comply with the regulations of the environment where the race is held.",
      ];
      if (language === "id") {
        return {
          title: "PERNYATAAN PELEPASAN TUNTUTAN",
          subtitle: smmSubtitle,
          nameLabel,
          dateLabel,
          paragraphs: [
            "Saya menyatakan telah membaca dan menyetujui keseluruhan pernyataan dalam dokumen ini, maka secara sadar Saya bertanggung jawab atas segala risiko yang terjadi selama dan setelah perlombaan berlangsung.",
            "Sehubungan dengan kepesertaan Saya di SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026, Saya secara sadar menyetujui pelepasan tanggung jawab atas segala risiko yang terjadi.",
            "Saya telah membaca dan menyetujui segala informasi terkait acara, peraturan serta informasi lainnya yang berkaitan dengan SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026.",
            { numbered: true, items: smmNumberedID },
          ],
        };
      }
      return {
        title: "RELEASE OF LIABILITY AND WAIVER OF CLAIMS",
        subtitle: smmSubtitle,
        nameLabel,
        dateLabel,
        paragraphs: [
          "I hereby declare that I have read and agreed to all the statements in this document. I consciously assume full responsibility for all risks occurring during and after the race. In connection with my participation in the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026, I knowingly agree to release all parties from liability for any risks that may occur.",
          "I have read and agreed to all event-related information, regulations, and other details pertaining to the SEMBALUN MOUNTAIN MARATHON FESTIVAL 2026.",
          { numbered: true, items: smmNumberedEN },
        ],
      };
    }

    if (language === "en") {
      const paragraphs = [
        `I hereby declare that I voluntarily and knowingly register myself as a participant in ${eventData.namaEvent} ${eventData.tahun}, organized by Mountain Marathon Festival (MMF), which will be held in ${eventData.lokasi.en}.`,
        `I understand that this event is physically and mentally demanding and involves potential risks such as extreme weather, difficult terrain, altitude, and other health hazards. Therefore, I confirm that I have adequately prepared myself and am in good health to participate.`,
        `I acknowledge that the event organizers, volunteers, sponsors, and other related parties cannot be held legally responsible for injuries, accidents, loss, or other emergencies that may occur during my participation. I accept full responsibility for my personal safety and health during the event and release the organizers from any legal claims resulting from or related to my participation.`,
        `I commit to:`,
        [
          "Following all rules and instructions from organizers and authorities.",
          "Maintaining sportsmanship, safety, and order throughout the event.",
          "Not engaging in actions that harm myself, other participants, organizers, or the environment.",
        ],
        `I grant Mountain Marathon Festival (MMF) full permission to use any documentation (photos, videos, or other media) that include my image or identity for publication, promotion, and documentation purposes without requiring further consent.`,
        `By signing this declaration, I confirm I have read, understood, and agree to all terms voluntarily and without coercion. This declaration represents my commitment and responsibility as a participant in ${eventData.namaEvent}.`,
      ];
      return { title: "DECLARATION LETTER", subtitle: null, paragraphs, nameLabel, dateLabel };
    } else {
      const paragraphs = [
        `Dengan ini saya menyatakan bahwa saya secara sukarela dan sadar mendaftarkan diri sebagai peserta dalam kegiatan ${eventData.namaEvent} tahun ${eventData.tahun} yang diselenggarakan oleh Mountain Marathon Festival (MMF), yang akan dilaksanakan di ${eventData.lokasi.id}.`,
        `Saya memahami bahwa kegiatan ini menantang secara fisik dan mental, serta memiliki potensi risiko, seperti kondisi cuaca ekstrem, kontur medan yang berat, ketinggian, dan gangguan kesehatan lainnya. Oleh karena itu, saya menyatakan bahwa saya telah mempersiapkan diri secara optimal untuk mengikuti kegiatan ini dan dalam kondisi kesehatan yang baik.`,
        `Saya menyadari bahwa panitia penyelenggara, relawan, sponsor, maupun pihak-pihak terkait lainnya tidak bertanggung jawab secara hukum atas risiko cedera, kecelakaan, kehilangan, atau kejadian darurat lainnya yang mungkin saya alami selama mengikuti rangkaian kegiatan. Seluruh tanggung jawab atas keselamatan dan kesehatan pribadi selama kegiatan merupakan tanggung jawab saya sendiri, dan saya melepaskan panitia dari segala bentuk tuntutan hukum atas kejadian yang mungkin timbul selama atau akibat keikutsertaan saya dalam kegiatan ini.`,
        `Saya berkomitmen untuk:`,
        [
          "Mengikuti seluruh peraturan dan arahan dari panitia dan pihak berwenang.",
          "Menjaga sportivitas, keamanan, dan ketertiban selama kegiatan berlangsung.",
          "Tidak melakukan tindakan yang merugikan diri sendiri, peserta lain, panitia, maupun lingkungan sekitar.",
        ],
        `Saya juga memberikan izin penuh kepada panitia Mountain Marathon Festival (MMF) untuk menggunakan dokumentasi (foto, video, atau lainnya) yang memuat identitas atau citra saya untuk keperluan publikasi, promosi, dan dokumentasi kegiatan tanpa memerlukan izin tambahan.`,
        `Dengan ini, saya menyatakan telah membaca, memahami, dan menyetujui seluruh isi pernyataan ini dengan sadar, sukarela, dan tanpa tekanan dari pihak mana pun. Pernyataan ini saya buat sebagai bentuk komitmen dan tanggung jawab saya sebagai peserta dalam ${eventData.namaEvent}.`,
      ];
      return { title: "SURAT PERNYATAAN", subtitle: null, paragraphs, nameLabel, dateLabel };
    }
  };

  const generatePDF = async () => {
    if (!isReady || !printRef.current || !eventData) {
      alert("Dokumen belum siap untuk diunduh. Silakan tunggu sebentar.");
      return;
    }

    const content = buildContent(lang);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginLeft = 20;
    const marginTop = 20;
    const lineHeight = 7;
    const bulletIndent = 5;
    let yPosition = marginTop;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(content.title, 105, yPosition, { align: "center" });
    yPosition += lineHeight;
    if (content.subtitle) {
      pdf.setFontSize(13);
      pdf.setFont("helvetica", "bold");
      pdf.text(content.subtitle, 105, yPosition, { align: "center" });
      yPosition += lineHeight;
    }
    yPosition += lineHeight;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");

    // Nama & Tanggal (localized)
    const today = new Date();
    const dateStr = formatDateForLang(today, lang);
    pdf.text(`${content.nameLabel}: ${namaLengkap}`, marginLeft, yPosition);
    yPosition += lineHeight;
    pdf.text(`${content.dateLabel}: ${dateStr}`, marginLeft, yPosition);
    yPosition += lineHeight * 2;

    content.paragraphs.forEach((item) => {
      if (item && typeof item === "object" && item.numbered && Array.isArray(item.items)) {
        item.items.forEach((bullet, idx) => {
          const prefix = `${idx + 1}. `;
          const lines = pdf.splitTextToSize(prefix + bullet, 165);
          lines.forEach((line) => {
            if (yPosition + lineHeight > 277) {
              pdf.addPage();
              yPosition = marginTop;
            }
            pdf.text(line, marginLeft + bulletIndent, yPosition);
            yPosition += lineHeight;
          });
          yPosition += 1;
        });
      } else if (Array.isArray(item)) {
        item.forEach((bullet) => {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(`\u2022 ${bullet}`, marginLeft + bulletIndent, yPosition);
          yPosition += lineHeight;
        });
      } else {
        const lines = pdf.splitTextToSize(item, 170);
        lines.forEach((line) => {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(line, marginLeft, yPosition);
          yPosition += lineHeight;
        });
      }
      yPosition += lineHeight;
    });

    if (yPosition + lineHeight * 3 > 277) {
      pdf.addPage();
      yPosition = marginTop;
    }
    const honord = lang === "en" ? "Sincerely," : "Hormat saya,";
    pdf.text(honord, 170, yPosition, { align: "right" });
    yPosition += lineHeight * 2;
    pdf.setFont("helvetica", "bold");
    pdf.text(namaLengkap, 170, yPosition, { align: "right" });

    const safeEventName = eventData.namaEvent.replace(/\s+/g, "_");
    const safeName = namaLengkap.replace(/\s+/g, "_");
    pdf.save(`Surat_Pernyataan_${safeEventName}_${safeName}_${lang}.pdf`);
  };

  if (!namaLengkap || !tanggal || !eventData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center text-gray-600 text-lg font-semibold p-6">
          Memuat data...
        </div>
      </div>
    );
  }

  const content = buildContent(lang);
  const today = new Date();
  const dateStr = formatDateForLang(today, lang);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white py-20 px-4">
      <div className="w-full max-w-[21cm]">
        <div className="flex justify-end mb-4">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              onClick={() => setLang("id")}
              className={`px-4 py-1 text-sm font-medium transition ${
                lang === "id"
                  ? "bg-slate-700 text-white rounded-l-md"
                  : "bg-white text-slate-700 border border-slate-200 rounded-l-md"
              }`}
            >
              Bahasa (ID)
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-4 py-1 text-sm font-medium transition ${
                lang === "en"
                  ? "bg-slate-700 text-white rounded-r-md"
                  : "bg-white text-slate-700 border border-slate-200 rounded-r-md"
              }`}
            >
              English
            </button>
          </div>
        </div>

        <div
          ref={printRef}
          className="bg-white p-4 sm:p-8 shadow-lg rounded-xl text-justify border border-gray-200 w-full mx-auto"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-2 text-gray-800">
            {content.title}
          </h2>
          {content.subtitle && (
            <h3 className="text-base sm:text-lg font-bold text-center mb-6 text-gray-800">
              {content.subtitle}
            </h3>
          )}

          <p className="mb-4 text-gray-700 text-sm sm:text-base">
            <strong className="text-gray-900">{content.nameLabel}:</strong>{" "}
            {namaLengkap}
          </p>
          <p className="mb-6 text-gray-700 text-sm sm:text-base">
            <strong className="text-gray-900">{content.dateLabel}:</strong>{" "}
            {dateStr}
          </p>

          {content.paragraphs.map((item, idx) => {
            if (item && typeof item === "object" && item.numbered && Array.isArray(item.items)) {
              return (
                <ol
                  key={idx}
                  className="mb-4 pl-6 space-y-2 text-gray-700 leading-relaxed text-sm sm:text-base"
                >
                  {item.items.map((li, i) => (
                    <li key={i} className="list-decimal">{li}</li>
                  ))}
                </ol>
              );
            }
            if (Array.isArray(item)) {
              return (
                <ul
                  key={idx}
                  className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base list-disc list-inside"
                >
                  {item.map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
              );
            }
            return (
              <p
                key={idx}
                className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base"
              >
                {item}
              </p>
            );
          })}

          <p className="mt-6 text-left text-gray-700 text-sm sm:text-base">
            {lang === "en" ? "Sincerely," : "Hormat saya,"}
          </p>
          <p className="mt-4 text-left font-bold text-gray-900 text-sm sm:text-base">
            {namaLengkap}
          </p>
        </div>

        <div className="flex justify-center mt-6">
          <button
            onClick={generatePDF}
            disabled={!isReady}
            className={`px-6 py-2 rounded-xl font-semibold text-white transition-colors duration-200 ${
              isReady
                ? "bg-slate-700 hover:bg-slate-800"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {lang === "en" ? "Download PDF" : "Unduh PDF"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuratPernyataan;
